import { timeZoneLabel } from '@league/shared/time';

/**
 * DATES ARE READ IN THE LEAGUE'S ZONE, NOT THE READER'S.
 *
 * Every session on this platform is stored as an instant. Turning one back into "Tuesday 14 October"
 * needs a zone, and there were two wrong answers in the code before this file existed:
 *
 *   `America/New_York` hardcoded — wrong for Nashville, which is a market the platform has today.
 *   `toLocaleDateString(undefined, …)` — the BROWSER's zone, which is worse, because it is right on
 *   the machine you test on and wrong on a director's laptop that a trip left on Pacific.
 *
 * So the zone is always passed. It comes down with the league, from its market, and there is no
 * default: a caller with no zone to hand is a caller who has not yet worked out whose date this is.
 */

/** A date as a reader in the league's city would write it. */
export const leagueDate = (
  value: string | Date,
  zone: string,
  opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' },
): string =>
  new Intl.DateTimeFormat('en-US', { ...opts, timeZone: zone }).format(
    typeof value === 'string' ? new Date(value) : value,
  );

/**
 * A clock time, with the zone named — "7:00 PM CT".
 *
 * The suffix is not decoration. A bare time is unreadable to anybody who does not already know
 * which zone it is in, and on a platform running leagues in several it is a guess.
 */
export const leagueTime = (value: string | Date, zone: string): string =>
  new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: zone,
    timeZoneName: 'short',
  }).format(typeof value === 'string' ? new Date(value) : value);

/**
 * "Mountain time" — said beside a start and stop time, so a director can see the times are the
 * host city's and not her computer's (owner, 2026-10-12). Arizona's label carries its daylight-saving
 * note, which a time does not need.
 */
export const zoneName = (zone: string): string => `${timeZoneLabel(zone).replace(/ \(.*\)$/, '')} time`;
